/**
 * Admin → Taken in agenda (agendablokken stap 2, Joris 27 okt 2026).
 *
 * Joris zet zelf per persoon aan wie zijn ingeplande taken als blokken in Outlook krijgt. Wie het
 * zelf heeft uitgezet (Mijn profiel) staat erbij met "zelf uitgezet"; die schakelaar is dicht, want
 * de beheerder overschrijft een eigen keuze niet. Stagiairs, PL's en freelancers met een eigen adres
 * staan erin met een waarschuwing; gedeelde adressen niet.
 *
 * Per persoon een proefrun: wat de planner nu zou zetten (blokken, uren, overvolle dagen). Schrijft niets.
 * Engine: api/routes/agenda_blokken_beheer.py (develop via de voordeur, main via de Node-proxy).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Collapse, Switch, Tooltip, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { SectionHeader, T } from '../adminSettingsCommon';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../../utils/ongedaan';

const URL_BASIS = `${BACKEND_URL}/Api/agenda-blokken/beheer`;

async function lees(r) {
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || d.error || `HTTP ${r.status}`);
  return d;
}

const dagLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
const tijd = (iso) => String(iso || '').slice(11, 16);
const uren = (u) => `${Number(u || 0).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} u`;

export function Proefrun({ res }) {
  if (!res) return null;
  const dagen = (res.dagen || []).filter((d) => (d.blokken || []).length || d.tekort || d.staat);
  return (
    <Box sx={{ pl: 1, pr: 1, pb: 1.5, pt: 0.5 }} data-testid="proefrun">
      <Typography sx={{ fontSize: '0.8rem', color: T.secondary, mb: 1 }}>
        {res.blokken} blokken · {uren(res.uren)} werk
        {res.overvol?.length ? ` · overvol op ${res.overvol.map((o) => `${dagLabel(o.dag)} (+${uren(o.tekortUren)})`).join(', ')}` : ' · geen overvolle dagen'}
      </Typography>
      {dagen.map((d) => (
        <Box key={d.dag} sx={{ display: 'flex', gap: 1.5, py: 0.25, fontSize: '0.8rem' }}>
          <Box sx={{ width: 90, flexShrink: 0, color: T.secondary }}>{dagLabel(d.dag)}</Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {d.staat ? <span style={{ color: T.secondary }}>{d.staat}</span>
              : (d.blokken || []).map((b, i) => (
                <div key={i}>{tijd(b.start)}–{tijd(b.eind)} {b.project ? `${b.project} · ` : ''}{b.taak}</div>
              ))}
            {d.tekort > 0 && <div style={{ color: '#b45309' }}>past niet: {uren(d.tekort / 60)}</div>}
          </Box>
        </Box>
      ))}
      {(res.geenPlek || []).length > 0 && (
        <Typography sx={{ fontSize: '0.78rem', color: T.secondary, mt: 0.5 }}>
          Geen plek in het bereik: {res.geenPlek.map((g) => g.taak).join(', ')}
        </Typography>
      )}
      {(res.fouten || []).length > 0 && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{res.fouten.join(' · ')}</Alert>}
    </Box>
  );
}

function Rij({ p, bezig, kanAan, onZet, onProef, proef, open }) {
  // Aanzetten kan niet bij een eigen opt-out, buiten de lijst, of zolang stap 2 uit staat; uitzetten altijd.
  const geenAan = p.zelfUit || Boolean(p.uitgesloten) || !kanAan;
  return (
    <Box sx={{ borderBottom: `1px solid ${T.surfaceHigh}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
        <Tooltip title={p.zelfUit ? 'Zelf uitgezet in Mijn profiel — dat overschrijf je niet' : ''}>
          <span>
            <Switch size="small" checked={Boolean(p.beheerAan)} disabled={bezig || (!p.beheerAan && geenAan)}
              inputProps={{ 'aria-label': `Taken in agenda voor ${p.naam}` }}
              onChange={(e) => onZet(p, e.target.checked)} />
          </span>
        </Tooltip>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: T.text }}>{p.naam}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: T.secondary }}>
            {[p.functie || p.type, p.zelfUit ? 'zelf uitgezet' : null, p.uitgesloten || null,
              p.waarschuwingen?.length ? `let op: ${p.waarschuwingen.join(', ').toLowerCase()}` : null,
              p.aan && p.porties ? `${p.porties} blokken gepland` : null,
              p.tekortDagen?.length ? `${p.tekortDagen.length} overvolle dag(en)` : null].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Button size="small" onClick={() => onProef(p)} disabled={proef === 'laden'}
          sx={{ textTransform: 'none', color: T.secondary }}>
          {proef === 'laden' ? <CircularProgress size={14} /> : (open ? 'Verberg' : 'Proefrun')}
        </Button>
      </Box>
      <Collapse in={open && proef && proef !== 'laden'} unmountOnExit>
        <Proefrun res={proef && proef !== 'laden' ? proef : null} />
      </Collapse>
    </Box>
  );
}

export default function AgendaBlokkenBeheer() {
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [proef, setProef] = useState({});
  const [open, setOpen] = useState(null);

  const laad = useCallback(async () => {
    try { setData(await lees(await authFetch(URL_BASIS))); setFout(null); }
    catch (e) { setFout(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  const zet = useCallback(async (email, aan) => {
    const r = await authFetch(`${URL_BASIS}/${encodeURIComponent(email)}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ aan }),
    });
    return lees(r);
  }, []);

  const onZet = async (p, aan) => {
    const vorige = Boolean(p.beheerAan);
    setBezig(true);
    try {
      await zet(p.email, aan);
      await laad();
      biedOngedaanAan(aan ? `${p.naam} staat aan — blokken verschijnen binnen 10 minuten` : `${p.naam} staat uit — blokken worden opgeruimd`,
        async () => { await zet(p.email, vorige); await laad(); });
    } catch (e) { toast.error(e.message); }
    finally { setBezig(false); }
  };

  const onProef = async (p) => {
    if (open === p.email) { setOpen(null); return; }
    setOpen(p.email);
    if (proef[p.email] && proef[p.email] !== 'laden') return;
    setProef((x) => ({ ...x, [p.email]: 'laden' }));
    try {
      const res = await lees(await authFetch(`${URL_BASIS}/${encodeURIComponent(p.email)}/proefrun`, { method: 'POST' }));
      setProef((x) => ({ ...x, [p.email]: res }));
    } catch (e) {
      setProef((x) => ({ ...x, [p.email]: undefined }));
      setOpen(null);
      toast.error(`Proefrun ${p.naam}: ${e.message}`);
    }
  };

  return (
    <Box sx={{ maxWidth: 820 }}>
      <SectionHeader title="Taken in agenda"
        subtitle="Wie krijgt zijn ingeplande taken als blokken in Outlook. Eén blok per dag, over meetings heen, nooit na het einde van de werkdag." />
      {fout && <Alert severity="error" sx={{ mb: 2 }}>{fout}</Alert>}
      {!data && !fout && <CircularProgress size={22} />}
      {data && !data.stap2 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          De dagplanning staat nog uit (AGENDA_BLOKKEN_STAP2). Proefruns werken al; aanzetten kan na de omschakeling.
        </Alert>
      )}
      {data && data.pauze && <Alert severity="warning" sx={{ mb: 2 }}>Pauze staat aan: er wordt niets in agenda's geschreven.</Alert>}
      {data && (
        <>
          {data.personen.map((p) => (
            <Rij key={p.email} p={p} bezig={bezig} kanAan={Boolean(data.stap2)} onZet={onZet} onProef={onProef} proef={proef[p.email]} open={open === p.email} />
          ))}
          {(data.nietMeerGeschikt || []).length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: T.secondary, mb: 0.5 }}>Niet meer geschikt</Typography>
              {data.nietMeerGeschikt.map((p) => (
                <Rij key={p.email} p={p} bezig={bezig} kanAan={Boolean(data.stap2)} onZet={onZet} onProef={onProef} proef={proef[p.email]} open={open === p.email} />
              ))}
            </Box>
          )}
          {Object.keys(data.uitgesloten || {}).length > 0 && (
            <Typography sx={{ fontSize: '0.75rem', color: T.secondary, mt: 2 }}>
              Niet in de lijst: {Object.entries(data.uitgesloten).map(([r, n]) => `${n}× ${r}`).join(', ')}.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
